import type { MinMax } from '../../core/types/min-max.js';
import {
  XYAxisTypeCategorical,
  XYAxisTypeLinear,
  XYAxisTypeLog,
  XYAxisTypeTime,
} from '../types/axis.js';
import type {
  AxisIdToScaleDomain,
  CategoryName,
  XYChartAxisInternal,
} from '../types/xy-chart-internals.js';
import {
  type AreaDatapoint,
  type BarDatapoint,
  type DotDatapoint,
  type LineDatapoint,
  type RectDatapoint,
  type XYAccessorDataTypes,
} from '../types/xy-chart.js';

function getMinMax(
  axisId: string,
  coordinate0: number | undefined,
  coordinate1: number | undefined,
  axisIdToScaleDomain: AxisIdToScaleDomain,
): MinMax {
  const minMax = (axisIdToScaleDomain.get(axisId) ?? {
    min: coordinate0,
    max: coordinate1,
  }) as MinMax;

  let min = minMax.min;
  let max = minMax.max;

  if (coordinate0 !== undefined) {
    if (min === undefined || coordinate0 < min) {
      min = coordinate0;
    }
    if (max === undefined || coordinate0 > max) {
      max = coordinate0;
    }
  }
  if (coordinate1 !== undefined) {
    if (min === undefined || coordinate1 < min) {
      min = coordinate1;
    }
    if (max === undefined || coordinate1 > max) {
      max = coordinate1;
    }
  }

  minMax.min = min;
  minMax.max = max;

  return minMax;
}

function getCategoricalDomain(
  axisId: string,
  v0: string,
  v1: string,
  axisIdToScaleDomain: AxisIdToScaleDomain,
) {
  const categoricalSet = (axisIdToScaleDomain.get(axisId) ??
    new Set()) as Set<CategoryName>;

  if (v0) {
    categoricalSet.add(v0);
  }
  if (v1) {
    categoricalSet.add(v1);
  }

  return categoricalSet;
}

type CalculateLinearDomain = (
  axisId: string,
  a0: XYAccessorDataTypes | undefined,
  a1: XYAccessorDataTypes | undefined | unknown,
  axisIdToScaleDomain: AxisIdToScaleDomain,
) => MinMax;

type CalculateCategoricalDomain = (
  axisId: string,
  a0: XYAccessorDataTypes | undefined,
  a1: XYAccessorDataTypes | undefined | unknown,
  axisIdToScaleDomain: AxisIdToScaleDomain,
) => Set<CategoryName>;

type CalculateAxesDomainByType = {
  [XYAxisTypeLinear]: CalculateLinearDomain;
  [XYAxisTypeLog]: CalculateLinearDomain;
  [XYAxisTypeCategorical]: CalculateCategoricalDomain;
  [XYAxisTypeTime]: CalculateLinearDomain;
};

const calculateAxesDomainsMap: CalculateAxesDomainByType = {
  [XYAxisTypeLinear]: (axisId, a0, a1, axisIdToScaleDomain) => {
    return getMinMax(axisId, a0 as number, a1 as number, axisIdToScaleDomain);
  },
  [XYAxisTypeLog]: (axisId, a0, a1, axisIdToScaleDomain) => {
    return getMinMax(axisId, a0 as number, a1 as number, axisIdToScaleDomain);
  },
  [XYAxisTypeCategorical]: (axisId, a0, a1, axisIdToScaleDomain) => {
    return getCategoricalDomain(
      axisId,
      a0 as string,
      a1 as string,
      axisIdToScaleDomain,
    );
  },
  [XYAxisTypeTime]: (axisId, a0, a1, axisIdToScaleDomain) => {
    return getMinMax(
      axisId,
      a0?.valueOf() as number,
      a1?.valueOf() as number,
      axisIdToScaleDomain,
    );
  },
};

//TODO: types issues, rect and dot series not share the same types, x1 and y1 are working cause of unknown type
export function calculateAxesDomains(
  xAxis: XYChartAxisInternal,
  yAxis: XYChartAxisInternal,
  datapoint:
    | LineDatapoint
    | DotDatapoint
    | BarDatapoint
    | RectDatapoint
    | AreaDatapoint,
  axisIdToScaleDomain: AxisIdToScaleDomain,
  isSeriesVisible: boolean,
) {
  const xDomain = calculateAxesDomainsMap[xAxis.type](
    xAxis.id,
    datapoint.x0,
    datapoint.x1,
    axisIdToScaleDomain,
  );

  const yDomain =
    isSeriesVisible || yAxis.type === XYAxisTypeCategorical
      ? calculateAxesDomainsMap[yAxis.type](
          yAxis.id,
          datapoint.y0,
          datapoint.y1,
          axisIdToScaleDomain,
        )
      : axisIdToScaleDomain.get(yAxis.id) ?? {};

  return { xDomain, yDomain };
}
